import ResponseCode from "@/enums/ResponseCode";
import IBasicResponse from "@/interfaces/Data/IBasicResponse";
import { ILoginResponse } from "@/interfaces/Data/Login";
import System from "@/static/public";
import { getLocalStore, getSession, removeLocalStore, removeSession, setSession } from "@/utils/Session";
import { QueryReturnValue } from "@reduxjs/toolkit/dist/query/baseQueryTypes";
import {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
  FetchBaseQueryMeta,
  fetchBaseQuery,
} from "@reduxjs/toolkit/query/react";
import { Mutex } from "async-mutex";
const mutex = new Mutex();

const baseQuery = fetchBaseQuery({
  baseUrl: process.env.REACT_APP_BASE_URL,
  mode: "cors",
  prepareHeaders: (headers) => {
    const access_token = getSession(System.ADMIN_TOKEN);
    if (access_token) {
      headers.set("Authorization", `Bearer ${access_token}`);
    }
    if (headers.get("Content-Type") === null) {
      headers.set("Content-Type", `application/json`);
    } else if (headers.get("Content-Type") === 'multipart/form-data') {
      // 當上傳圖片時，刪除 Content-Type ，由系統自動添加
      // https://bobbyhadz.com/blog/error-multipart-boundary-not-found-in-express-js
      headers.delete("Content-Type");
    }
    return headers;
  },
});

export const baseQueryWithIntercept: BaseQueryFn<
  string | FetchArgs,
  IBasicResponse<any>,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {

  await mutex.waitForUnlock();

  let result: QueryReturnValue<any, FetchBaseQueryError, FetchBaseQueryMeta> =
    await baseQuery(args, api, extraOptions);

  const { data, error, meta } = result;

  if (error) {
    if (!mutex.isLocked()) {

      const release = await mutex.acquire();
      try {
        const { status } = error;
        const { request } = meta as FetchBaseQueryMeta;
        const url: string = request.url;
        let apiPath = url.split("/");
        if (
          (status === 401 && apiPath[apiPath.length - 1] === "refresh_token") ||
          (status === 999 && apiPath[apiPath.length - 1] === "refresh_token")
        ) {
          window.location.href = "/login";
        } else if (status === 401 && apiPath[apiPath.length - 1] !== "login") {

          const refreshToken = getSession(System.ADMIN_REFRESH_TOKEN) || getLocalStore(System.ADMIN_REFRESH_TOKEN);
          const refreshResult = await baseQuery(
            {
              url: "/auth/api/refresh_token/",
              method: "post",
              body: { refresh_token: refreshToken },
            },
            api,
            extraOptions
          );
          const { data } = refreshResult;
          if (data) {
            // Retry the initial query
            setSession(
              System.ADMIN_TOKEN,
              (data as IBasicResponse<ILoginResponse>).data.access_token
            );
            result = await baseQuery(args, api, extraOptions);
          } else {
            removeSession(System.ADMIN_TOKEN);
            removeSession(System.ADMIN_REFRESH_TOKEN);
            removeLocalStore(System.ADMIN_REFRESH_TOKEN);
            window.location.href = "/login";
          }
        }
      } catch (error) {
      } finally {
        release();
      }
    } else {
      await mutex.waitForUnlock();
      result = await baseQuery(args, api, extraOptions)
    }
  }

  if (data && (data as IBasicResponse<any>) && data.code !== ResponseCode.SUCCESS) {
    return {
      error: {
        status: "CUSTOM_ERROR",
        data: (data as IBasicResponse<any>).code,
        error: (data as IBasicResponse<any>).message!!,
      },
    };
  }
  return result;
};
